/**
 * RuleService - Handles discipline rule operations
 */

import { collection, query, onSnapshot } from 'firebase/firestore';
import { db } from '../../../firebase';
import { RuleRepository } from '../repositories/RuleRepository';
import { loadPhasePolicy } from '../../domain/loadPhasePolicy';

export class RuleService {
  constructor(ruleRepo = null) {
    this.ruleRepo = ruleRepo || new RuleRepository();
    this.collectionName = this.ruleRepo.collectionName || 'disciplineRules';
  }

  // Delegate CRUD operations to repository
  async createRule(ruleData) {
    return this.ruleRepo.create(ruleData);
  }

  async getActiveRules(schoolId) {
    return this.ruleRepo.getActiveRules(schoolId);
  }

  async getPhasePolicy(schoolId) {
    // Other Domain adapters opt in as their participating paths are completed.
    return this.collectionName === 'disciplineRules' ? loadPhasePolicy(schoolId) : null;
  }

  async getRulesByType(type, activeOnly = true, schoolId) {
    return this.ruleRepo.getRulesByType(type, activeOnly, schoolId);
  }

  async updateRule(ruleId, updates) {
    return this.ruleRepo.update(ruleId, updates);
  }

  async deleteRule(ruleId, schoolId) {
    // Hard delete - actually remove the document
    return this.ruleRepo.delete(ruleId, schoolId);
  }

  async updateSortOrders(updates) {
    return this.ruleRepo.updateSortOrders(updates);
  }

  // Subscribe to rule changes
  subscribeRules(callback, filters = {}) {
    try {
      console.log('Setting up discipline rules subscription...', { filters });

      // CRITICAL: Never query without schoolId - would violate security rules
      if (!filters.schoolId) {
        console.error('[RuleService] Attempted to subscribe without schoolId - returning empty rules');
        callback([]);
        return () => {}; // Return no-op unsubscribe
      }

      // CRITICAL: Use hierarchical path schools/{schoolId}/disciplineRules
      // Don't use where('schoolId', '==', ...) - the path enforces school scoping
      const constraints = [];

      // CRITICAL: Use hierarchical collection path
      const q = query(
        collection(db, 'schools', filters.schoolId, this.collectionName),
        ...constraints
      );

    return onSnapshot(
      q,
      (querySnapshot) => {
        try {
          console.log('Discipline rules snapshot received:', querySnapshot.docs.length, 'documents');
          const rules = querySnapshot.docs.map(doc => {
            const data = doc.data();
            console.log('Rule document:', doc.id, 'title:', data.title, 'type:', data.type, 'active:', data.active, 'sortOrder:', data.sortOrder);
            return {
              id: doc.id,
              ...doc.data()
            };
          });

          // Sort client-side, treating missing sortOrder as high number (appear last)
          rules.sort((a, b) => {
            const orderA = a.sortOrder ?? 999999;
            const orderB = b.sortOrder ?? 999999;
            return orderA - orderB;
          });

          console.log('Calling callback with', rules.length, 'rules (sorted)');
          callback(rules);
        } catch (err) {
          console.error('[RuleService] Error processing snapshot:', err);
          callback([]);
        }
      },
      (error) => {
        console.error('[RuleService] Subscription error:', error);
        callback([]);
      }
    );
    } catch (error) {
      console.error('[RuleService] CRITICAL: Error setting up subscription:', error);
      callback([]);
      return () => {}; // Return no-op unsubscribe
    }
  }
}
